// 4 free tools beyond the Authority Score. Each tool defines questions, weights, and
// interpretation bands. Logic lives in the shared Tool page renderer.

export const TOOLS = {
  "content-gap": {
    slug: "content-gap",
    name: "Content Gap Calculator",
    eyebrow: "Free Tool",
    blurb: "How much organic content do you actually own — and where's the gap?",
    estMinutes: 2,
    questions: [
      { key: "blog_articles", q: "How many SEO articles do you currently have published?", options: [
        { l: "0", v: 0 }, { l: "1–10", v: 5 }, { l: "11–50", v: 15 }, { l: "51–150", v: 25 }, { l: "150+", v: 35 },
      ]},
      { key: "publishing_cadence", q: "How often do you publish new long-form content?", options: [
        { l: "Never", v: 0 }, { l: "A few times per year", v: 5 }, { l: "Monthly", v: 15 }, { l: "Weekly", v: 25 },
      ]},
      { key: "content_hubs", q: "How many topical content hubs do you maintain?", options: [
        { l: "None", v: 0 }, { l: "1", v: 6 }, { l: "2–3", v: 12 }, { l: "4+", v: 18 },
      ]},
      { key: "keyword_strategy", q: "Do you target specific keywords?", options: [
        { l: "No strategy", v: 0 }, { l: "Loose intuition", v: 6 }, { l: "Documented keyword map", v: 14 },
      ]},
    ],
    biggestGap: (answers) => {
      if ((answers.blog_articles ?? 0) < 10) return "You have almost no owned SEO content. Search engines cannot find or rank you, so every visitor must be paid for or referred.";
      if ((answers.publishing_cadence ?? 0) < 10) return "Your publishing cadence is too low to compound. Authority compounds with consistency.";
      if ((answers.content_hubs ?? 0) < 6) return "You don't have topical hubs. Without hubs, individual articles can't reinforce each other in search.";
      return "Your keyword strategy is the weakest link — without a keyword map, content is published into a void.";
    },
    nextStep: (score) => {
      if (score < 30) return "Start with 1 topical hub and 10 articles around your strongest niche. Use the Authority Blueprint to plan it.";
      if (score < 60) return "Add 2 more hubs and double your monthly publishing cadence.";
      return "Move from publishing to optimization — refresh top performers and build internal linking.";
    },
  },

  "asset-checklist": {
    slug: "asset-checklist",
    name: "Authority Asset Checklist",
    eyebrow: "Free Tool",
    blurb: "Score yourself against the 7 Authority Assets in AuthorityOS.",
    estMinutes: 2,
    questions: [
      { key: "seo", q: "SEO content engine?", options: [{ l: "None", v: 0 }, { l: "Some articles", v: 7 }, { l: "Active hub strategy", v: 14 }]},
      { key: "tools", q: "Free tools that capture leads?", options: [{ l: "None", v: 0 }, { l: "One", v: 7 }, { l: "Multiple", v: 14 }]},
      { key: "lead_magnet", q: "Lead magnet?", options: [{ l: "None", v: 0 }, { l: "One", v: 7 }, { l: "Multiple", v: 14 }]},
      { key: "newsletter", q: "Email newsletter?", options: [{ l: "None", v: 0 }, { l: "Sporadic", v: 7 }, { l: "Consistent cadence", v: 14 }]},
      { key: "product", q: "Digital product?", options: [{ l: "None", v: 0 }, { l: "One", v: 7 }, { l: "Product ladder", v: 14 }]},
      { key: "ai", q: "AI assistant?", options: [{ l: "None", v: 0 }, { l: "Generic chatbot", v: 7 }, { l: "Trained on expertise", v: 14 }]},
      { key: "distribution", q: "Distribution system?", options: [{ l: "Random", v: 0 }, { l: "Some repurposing", v: 8 }, { l: "Full system", v: 16 }]},
    ],
    biggestGap: (a) => {
      const lowest = Object.entries(a).sort((x, y) => x[1] - y[1])[0];
      const labels = { seo: "SEO content", tools: "Free tools", lead_magnet: "Lead magnet", newsletter: "Email newsletter", product: "Digital product", ai: "AI assistant", distribution: "Distribution" };
      return `Your weakest asset is your ${labels[lowest?.[0]] || "core asset stack"}. Without it the rest of the system can't compound.`;
    },
    nextStep: () => "Pick the single weakest asset and commit to building a minimum-viable version in 30 days. The Authority Blueprint sequences these for you.",
  },

  "offer-positioning": {
    slug: "offer-positioning",
    name: "Offer Positioning Scorecard",
    eyebrow: "Free Tool",
    blurb: "Is your core offer positioned for authority — or competing on commodity?",
    estMinutes: 2,
    questions: [
      { key: "niche", q: "How specific is your niche?", options: [{ l: "General (e.g. consultant)", v: 0 }, { l: "Industry-specific", v: 10 }, { l: "Industry + role + outcome", v: 20 }]},
      { key: "outcome", q: "Is your outcome measurable in a single sentence?", options: [{ l: "Not really", v: 0 }, { l: "Sort of", v: 10 }, { l: "Yes, one sentence", v: 20 }]},
      { key: "price", q: "Have you raised prices in the last 12 months?", options: [{ l: "No, lowered or same", v: 0 }, { l: "Raised once", v: 10 }, { l: "Raised multiple times", v: 20 }]},
      { key: "demand", q: "Where does new business come from?", options: [{ l: "Cold outreach", v: 0 }, { l: "Some inbound", v: 10 }, { l: "Mostly inbound", v: 20 }]},
      { key: "differentiator", q: "Can you state your differentiator in 6 words?", options: [{ l: "No", v: 0 }, { l: "Approximately", v: 10 }, { l: "Yes, sharp and clear", v: 20 }]},
    ],
    biggestGap: (a) => {
      if ((a.niche ?? 0) < 10) return "Your niche is too broad. Authority compounds in niches where you can credibly be top-five.";
      if ((a.outcome ?? 0) < 10) return "Your outcome isn't measurable. People buy outcomes — not capabilities.";
      if ((a.differentiator ?? 0) < 10) return "Your differentiator isn't sharp enough to be remembered or referred.";
      return "Pricing inertia is dragging you down — your offer is positioned below its true value.";
    },
    nextStep: () => "Rewrite your one-line positioning statement: 'I help [specific person] achieve [measurable outcome] without [common objection].' Then test it on five recent clients.",
  },

  "audience-ownership": {
    slug: "audience-ownership",
    name: "Audience Ownership Score",
    eyebrow: "Free Tool",
    blurb: "Do you actually own your audience — or do you rent it from platforms?",
    estMinutes: 2,
    questions: [
      { key: "email_size", q: "How large is your email list?", options: [{ l: "None", v: 0 }, { l: "<500", v: 8 }, { l: "500–2,000", v: 16 }, { l: "2,000–10,000", v: 24 }, { l: "10,000+", v: 32 }]},
      { key: "email_engagement", q: "Average open rate?", options: [{ l: "Don't know", v: 0 }, { l: "<20%", v: 5 }, { l: "20–35%", v: 12 }, { l: "35%+", v: 20 }]},
      { key: "platform_dependence", q: "What percent of revenue depends on a platform you don't own?", options: [{ l: "100%", v: 0 }, { l: "50–99%", v: 8 }, { l: "10–49%", v: 16 }, { l: "<10%", v: 24 }]},
      { key: "direct_channels", q: "How many direct channels do you own?", options: [{ l: "0", v: 0 }, { l: "1 (email)", v: 8 }, { l: "2 (email + community)", v: 16 }, { l: "3+", v: 24 }]},
    ],
    biggestGap: (a) => {
      if ((a.email_size ?? 0) < 10) return "Your email list is too small. Without an owned channel a single algorithm change can break your business.";
      if ((a.platform_dependence ?? 0) < 10) return "You are renting your audience from a platform you don't control. That's the single highest business risk.";
      if ((a.direct_channels ?? 0) < 10) return "You only have one owned channel. Two is the minimum for resilience.";
      return "Engagement is your weak link — you have the audience but they're not active. The compound effect is muted.";
    },
    nextStep: () => "Build or refresh one lead magnet this month and drive every social touchpoint to it. Email is the most resilient asset in the system.",
  },
};

export const TOOL_LIST = Object.values(TOOLS);

export const interpret = (score) => {
  if (score <= 30) return { label: "Critical Gap", bg: "#1a0808", color: "#eb5a5a" };
  if (score <= 55) return { label: "Significant Gap", bg: "#1a0f08", color: "#ba7a30" };
  if (score <= 74) return { label: "Moderate Gap", bg: "#14140a", color: "#bba030" };
  if (score <= 89) return { label: "Small Gap", bg: "#0a1a0a", color: "#3aab5a" };
  return { label: "Authority Leader", bg: "#1a0a08", color: "#FF6575" };
};
